import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import {
  BranchService,
  CustomerService,
  DistributorService,
  EmployeeService,
  ExpenceCategoryService,
  OperationDetailService, PaymentTypePipe,
  ProductCategoryService,
  ProductService,
  ServiceCategoryService,
  ServiceService,
  trackByIndex,
  User
} from '@beautyapp/core';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { switchMap, takeUntil } from 'rxjs/operators';
import { OperationDetailComponent } from '../operation-detail/operation-detail.component';
import endOfMonth from 'date-fns/endOfMonth';
import startOfMonth from 'date-fns/startOfMonth';
import startOfYear from 'date-fns/startOfYear';
import subMonths from 'date-fns/subMonths';
import endOfYear from 'date-fns/endOfYear';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { format } from 'date-fns';
import { UserViewComponent } from '@beautyapp/users';
import { CommonModule } from "@angular/common";
import { NzSpaceModule } from "ng-zorro-antd/space";
import { NzTableModule } from "ng-zorro-antd/table";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzDatePickerModule } from "ng-zorro-antd/date-picker";
import { NzTooltipDirective, NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzGridModule } from "ng-zorro-antd/grid";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzTagModule } from "ng-zorro-antd/tag";
import { NzPipesModule } from "ng-zorro-antd/pipes";
import { NzCardModule } from "ng-zorro-antd/card";
import { NzInputModule } from "ng-zorro-antd/input";
import { InputComponent, SelectComponent, SelectSearchComponent } from '@beautyapp/form';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NgxPermissionsModule } from 'ngx-permissions';

@Component({
    selector: 'beautyapp-operation-detail-list',
    templateUrl: './operation-detail-list.component.html',
    styleUrls: ['./operation-detail-list.component.scss'],
    standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    NzSpaceModule,
    NzTableModule,
    NzButtonModule,
    NzIconModule,
    NzDatePickerModule,
    NzToolTipModule,
    NzGridModule,
    NzFormModule,
    NzSelectModule,
    NzTagModule,
    NzPipesModule,
    NzCardModule,
    NzInputModule,
    PaymentTypePipe,
    NzTooltipDirective,
    NzCheckboxModule,
    SelectComponent,
    NgxPermissionsModule,
    SelectSearchComponent,
    InputComponent
  ]
})
export class OperationDetailListComponent implements OnInit, OnDestroy {

  private service: OperationDetailService = inject(OperationDetailService);
  private branchService: BranchService = inject(BranchService);
  private drawerService: NzDrawerService = inject(NzDrawerService);
  private customerService: CustomerService = inject(CustomerService);
  private employeeService: EmployeeService = inject(EmployeeService);
  private productService: ProductService = inject(ProductService);
  private productCategoryService: ProductCategoryService = inject(ProductCategoryService);
  private distributorService: DistributorService = inject(DistributorService);
  private expenceCategoryService: ExpenceCategoryService = inject(ExpenceCategoryService);
  private serviceService: ServiceService = inject(ServiceService);
  private serviceCategoryService: ServiceCategoryService = inject(ServiceCategoryService);
  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private translateService: TranslateService = inject(TranslateService);

  ranges = {
    დღეს: [new Date(), new Date()],
    'მიმდინარე თვე': [startOfMonth(new Date()), endOfMonth(new Date())],
    'წინა თვე': [
      startOfMonth(subMonths(new Date(), 1)),
      endOfMonth(subMonths(new Date(), 1))
    ],
    'მიმდინარე წელი': [startOfYear(new Date()), endOfYear(new Date())]
  };

  dateform: UntypedFormGroup;
  date: any;
  dateFormat = 'dd/MM/yyyy';
  customers: User[] = [];
  serviceCategories: any;
  services: any;
  productCategories: any;
  distributors: any;
  products: any;
  expenceCategories: any;
  expenceSubCategories: any;

  loading = false;
  loadData: any[] = [];
  total = 1;
  paginatorPageSize = 10;
  paginatorPageIndex = 1;
  searchForm: UntypedFormGroup;
  searchValue?: string;
  branchId?: string;
  sub$: Subject<any> = new Subject<any>();
  branches$!: Observable<any>;

  // Rebuilt reactively on onLangChange / onTranslationChange so the dropdowns
  // never show raw keys (PAYMENTTYPE.1, OPERATION_TYPE.EVENT) when the
  // translation bundle hasn't loaded yet at construction time after a hard
  // reload.
  paymentTypes: { id: number; name: string }[] = this.buildPaymentTypes();
  operationType: { id: string; name: string }[] = this.buildOperationTypes();

  private readonly _translationSync = this.translateService.onLangChange
    .pipe(takeUntil(this.sub$))
    .subscribe(() => {
      this.paymentTypes = this.buildPaymentTypes();
      this.operationType = this.buildOperationTypes();
    });
  private readonly _translationLoadedSync = this.translateService.onTranslationChange
    .pipe(takeUntil(this.sub$))
    .subscribe(() => {
      this.paymentTypes = this.buildPaymentTypes();
      this.operationType = this.buildOperationTypes();
    });
  private readonly _translationDefaultLangSync = this.translateService.onDefaultLangChange
    .pipe(takeUntil(this.sub$))
    .subscribe(() => {
      this.paymentTypes = this.buildPaymentTypes();
      this.operationType = this.buildOperationTypes();
    });

  private buildPaymentTypes(): { id: number; name: string }[] {
    const t = (key: string) => this.translateService.instant(key);
    return [
      { id: 1, name: t('PAYMENTTYPE.1') },
      { id: 2, name: t('PAYMENTTYPE.2') },
      { id: 3, name: t('PAYMENTTYPE.3') },
      { id: 4, name: t('PAYMENTTYPE.4') },
      { id: 5, name: t('PAYMENTTYPE.5') },
      { id: 6, name: t('PAYMENTTYPE.6') },
      { id: 7, name: t('PAYMENTTYPE.7') },
      { id: 8, name: t('PAYMENTTYPE.8') },
      { id: 11, name: t('PAYMENTTYPE.11') },
    ];
  }

  private buildOperationTypes(): { id: string; name: string }[] {
    const t = (key: string) => this.translateService.instant(key);
    return [
      { id: 'EVENT', name: t('OPERATION_TYPE.EVENT') },
      { id: 'EVENT_SPEC', name: t('OPERATION_TYPE.EVENT_SPEC') },
      { id: 'SALARY', name: t('OPERATION_TYPE.SALARY') },
      { id: 'SALARY_OUT', name: t('OPERATION_TYPE.SALARY_OUT') },
      { id: 'SELL', name: t('OPERATION_TYPE.SELL') },
      { id: 'SPEND', name: t('OPERATION_TYPE.SPEND') },
      { id: 'BUY', name: t('OPERATION_TYPE.BUY') },
      { id: 'EXPENCE', name: t('OPERATION_TYPE.EXPENCE') },
      { id: 'BALANCEIN', name: t('OPERATION_TYPE.BALANCEIN') },
      { id: 'BALANCEOUT', name: t('OPERATION_TYPE.BALANCEOUT') },
      { id: 'BALANCERETURN', name: t('OPERATION_TYPE.BALANCERETURN') },
      { id: 'CASHIER_ADD', name: t('OPERATION_TYPE.CASHIER_ADD') },
      { id: 'CASHIER_OUT', name: t('OPERATION_TYPE.CASHIER_OUT') },
      { id: 'CONSIGNATION_PAY', name: t('OPERATION_TYPE.CONSIGNATION_PAY') },
      { id: 'REALIZATION', name: t('OPERATION_TYPE.REALIZATION') },
    ];
  }

  // "OPERATION_TYPE": {
  //   "SELL": "გაყიდვა",
  //   "SPEND": "გახარჯვა",
  //   "EVENT": "პროცედურა",
  //   "EVENT_SPEC": "სპეც. პროცედურა",
  //   "SALARY": "ხელფასი დარიცხვა",
  //   "SALARY_OUT": "ხელფასის გაცემა",
  //   "REALIZATION": "რეალიზაცია",
  //   "EXPENCE": "გასავალი",
  //   "BUY": "შესყიდვა",
  //   "BALANCEIN": "ბალანსის შევსება",
  //   "BALANCEOUT": "ბალანსის გახარჯვა",
  //   "BALANCERETURN": "ბალანსის დაბრუნება",
  //   "CASHIER_ADD": "თანხის დამატება",
  //   "CASHIER_OUT": "თანხის გამოტანა",
  //   "CONSIGNATION_PAY": "კონსიგნაციის დაფარვა"
  // },

  operationSource = [
    { id: 'BOOKING', name: this.translateService.instant('statistic.detailSearch.operationSource.reservation') },
    { id: 'SALARY', name: this.translateService.instant('statistic.detailSearch.operationSource.salary') },
    { id: 'REALIZATION', name: this.translateService.instant('statistic.detailSearch.operationSource.realization') },
    { id: 'EXPENCE', name: this.translateService.instant('statistic.detailSearch.operationSource.expense') },
    { id: 'PURCHASE', name: this.translateService.instant('statistic.detailSearch.operationSource.purchase') },
    { id: 'BALANCE', name: this.translateService.instant('statistic.detailSearch.operationSource.popOpBalance') },
    {
      id: 'BALANCERETURN',
      name: this.translateService.instant('statistic.detailSearch.operationSource.returnBalance')
    },
    { id: 'CASHIER', name: this.translateService.instant('statistic.detailSearch.operationSource.cashRegister') },
    { id: 'SERVICE_PACKAGE', name: 'სერვისის პაკეტი' }
  ];

  // NONE = 'NONE',
  // SELL = 'SELL',
  // SPEND = 'SPEND',
  // BUY = 'BUY',
  // BALANCEIN = 'BALANCEIN',
  // BALANCEOUT = 'BALANCEOUT',
  // SALARY = 'SALARY',
  // SALARY_OUT = 'SALARY_OUT',
  // EVENT = 'EVENT',
  // EVENT_SPEC = 'EVENT_SPEC',
  // REALIZATION = 'REALIZATION',
  // EXPENCE = 'EXPENCE'
  employees: any[] = [];
  trackByIndex = trackByIndex;

  get totalVat(): number {
    return this.loadData
      .filter(r => r.isVatIncluded && r.vatAmount)
      .reduce((sum, r) => sum + r.vatAmount, 0);
  }

  constructor() {
    this.searchForm = this.fb.group({
      search: null,
      branch: null
    });
    this.dateform = this.fb.group({
      date: { begin: startOfMonth(new Date()), end: new Date() },
      paymentType: null,
      serviceCategoryId: null,
      serviceId: null,
      employeeId: null,
      distributorId: null,
      userId: null,
      user: null,
      service: null,
      productCodeOrBarcode: null,
      productId: null,
      productCategoryId: null,
      productMeta: null,
      expenceMainCategoryId: null,
      expenceCategoryId: null,
      operationType: null,
      operationSource: null,
      isVatIncluded: false
    });
  }

  ngOnInit() {

    this.getBranches();
    this.getEmployees();
    this.getServices();
    this.getServiceCategories();
    this.getProductCategory();
    this.getProductDistributors();
    this.getExpenceCategories();


  }

  searchCustomer(search: any) {
    this.customerService
      .search(search)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.customers = res;
      });
  }

  getEmployees() {
    this.employeeService
      .getDropdown()
      .pipe(
        takeUntil(this.sub$),
        switchMap((res) => {
          return of(res.map((m: any) => ({ ...m, fullName: m.user?.fullName })));
        })
      )
      .subscribe((res) => {
        this.employees = res;
      });
  }

  getServices() {
    this.serviceService
      .dropdown()
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.services = res;
      });
  }

  getServiceCategories() {
    this.serviceCategoryService
      .dropdown()
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.serviceCategories = res;
      });
  }

  getExpenceCategories() {
    this.expenceCategoryService
      .dropdown()
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.expenceCategories = res;
      });
  }

  getChildExpense($event: any) {
    this.expenceCategoryService
      .getChildrens({ expenceCategoryId: $event })
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.expenceSubCategories = res;
      });
  }

  private getOperationDetails(param: any, isDeleted: boolean = false) {
    this.loading = true;
    const params = {
      search: param || null,
      page: this.paginatorPageIndex,
      pageSize: this.paginatorPageSize,
      isDeleted
    };
    this.service
      .get(params)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.loading = false;
        this.loadData = res.data;
        this.total = res.meta.itemCount;
      });
  }

  getBranches() {
    this.branches$ = this.branchService.getMyBranches();
  }

  paginatorChange(pageEvent: any) {
    this.paginatorPageSize = pageEvent.pageSize;
    this.paginatorPageIndex = pageEvent.pageIndex;
    this.getOperationDetails(this.dateform.value);
  }

  delete(item: any) {
    if (item.id) {
      this.service
        .delete(item.id)
        .pipe()
        .subscribe((res) => {
          this.getOperationDetails(this.dateform.value);
        });
    }
  }

  view(event: any) {
    const sourceName = this.translateService.instant('OPERATION_SOURCE.' + event.operationSource);
    const title = `${sourceName}  # ${event.id}`;

    const drawerRef = this.drawerService.create<OperationDetailComponent,
      { operationId: number },
      string>({
      nzTitle: title,
      nzContent: OperationDetailComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: {
        padding: '0 15px 15px 15px'
      },
      nzData: {
        operationId: event.id
      }
    });

    drawerRef.afterClose.subscribe((data) => {
      console.log(data);
      if (typeof data === 'string') {
        console.log(data);
      }
    });
  }

  search() {
    console.log(this.dateform.value);
    if (this.dateform.value) {
      this.paginatorPageIndex = 1;
      this.getOperationDetails(this.dateform.value);
    }
  }

  export() {
    const params = {
      search: this.dateform.value || null,
      isDeleted: false
    };
    return this.service.export('Operations.xlsx', params);
  }

  branchSelected($event: any) {
    this.branchId = $event;
    this.getOperationDetails(this.dateform.value);
  }

  clearSearch() {
    this.searchForm.reset();
    // Reset the date filter (the dateform AND the standalone range-picker model)
    // BEFORE re-searching, so Clear drops the date range from both the results
    // and the picker UI (beaut-283). Previously dateform was reset after search()
    // ran — so results stayed date-filtered — and `date` was never reset at all,
    // so the start/end picker stayed visually populated.
    this.dateform.reset();
    this.date = null;
    this.search();
    this.searchValue = undefined;
    this.branchId = undefined;
  }

  changeCalendar($event: (Date | null)[]) {
    this.dateform.get('date')?.setValue({
      begin: $event[0] && format($event[0], 'yyyy-MM-dd 00:00:00'),
      end: $event[1] && format($event[1], 'yyyy-MM-dd 23:59:59')
    });
    this.getOperationDetails(this.dateform.value);
  }

  searchProduct(search: string) {
    this.productService
      .search(search)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.products = res;
      });
  }

  getProductCategory() {
    this.productCategoryService
      .dropdown()
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.productCategories = res;
      });
  }

  getProductDistributors() {
    this.distributorService
      .dropdown()
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.distributors = res;
      });
  }

  getDetail(row: any) {
    if (row.operationDetails.length) {
      switch (row.operationSource) {
        case 'BOOKING':
          return row.operationDetails.find((f: any) => f.operationType === 'EVENT')
            .service?.name;
        case 'REALIZATION':
          return row.operationDetails[0].product?.name;
        default:
          return 'N/A';
      }
    }

    return 'N/A';
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }

  viewUser(user: User) {
    let title = `${user.firstname} ${user.lastname || ''}`;
    if (user.cardNo) {
      title += ` - # ${user.cardNo}`;
    }
    this.drawerService.create<UserViewComponent,
      { data: string },
      string>({
      nzTitle: title,
      nzContent: UserViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: {
        padding: '0 15px 15px 15px'
      },
      nzData: {
        data: user.id
      }
    });

  }
}
